import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { relativeTime } from '../lib/format';

interface NotificationRow {
  id: string;
  event: string;
  title: string;
  body: string;
  linkUrl: string | null;
  read: boolean;
  createdAt: string;
}

/** How often the badge refreshes. Cheap endpoint; this is just a count. */
const POLL_MS = 60_000;

/**
 * The notification bell.
 *
 * The badge polls a count-only endpoint rather than the whole list, so an idle
 * tab costs one small query a minute. The list itself is fetched when the panel
 * is opened, which is the only time anybody is looking at it.
 */
export function NotificationBell() {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationRow[] | null>(null);
  const container = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const refreshCount = useCallback(() => {
    api<{ unread: number }>('/my/notifications/unread-count')
      .then((result) => setUnread(result.unread))
      // A failing badge must never break the page it sits in.
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    refreshCount();
    const timer = window.setInterval(refreshCount, POLL_MS);
    return () => window.clearInterval(timer);
  }, [refreshCount]);

  // Close on an outside click or Escape, which is what every other menu does.
  useEffect(() => {
    if (!open) return undefined;

    function onPointerDown(event: MouseEvent) {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next) {
      setItems(null);
      api<{ items: NotificationRow[] }>('/my/notifications?pageSize=8')
        .then((result) => setItems(result.items))
        .catch(() => setItems([]));
    }
  }

  async function openItem(item: NotificationRow) {
    setOpen(false);
    if (!item.read) {
      try {
        await api(`/my/notifications/${item.id}/read`, { method: 'POST' });
        setUnread((count) => Math.max(0, count - 1));
      } catch {
        // Following the link matters more than recording the read.
      }
    }
    if (item.linkUrl) navigate(item.linkUrl);
  }

  async function markAll() {
    try {
      await api('/my/notifications/read-all', { method: 'POST' });
      setUnread(0);
      setItems((current) => current?.map((item) => ({ ...item, read: true })) ?? null);
    } catch {
      /* the badge will correct itself on the next poll */
    }
  }

  return (
    <div className="bell" ref={container}>
      <button
        className="bell__btn"
        onClick={toggle}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
      >
        <span aria-hidden="true">🔔</span>
        {unread > 0 ? (
          <span className="bell__badge">{unread > 99 ? '99+' : unread}</span>
        ) : null}
      </button>

      {open ? (
        <div className="bell__panel" role="dialog" aria-label="Notifications">
          <div className="bell__head">
            <strong>Notifications</strong>
            {unread > 0 ? (
              <button className="btn btn--quiet btn--sm" onClick={() => void markAll()}>
                Mark all read
              </button>
            ) : null}
          </div>

          {!items ? (
            <div className="spinner">Loading…</div>
          ) : items.length === 0 ? (
            <div className="empty">Nothing yet. We will tell you when something happens.</div>
          ) : (
            <ul className="bell__list">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    className={`bell__item${item.read ? '' : ' bell__item--unread'}`}
                    onClick={() => void openItem(item)}
                  >
                    <span className="bell__item-title">{item.title}</span>
                    <span className="bell__item-body">{item.body}</span>
                    <span className="bell__item-time">{relativeTime(item.createdAt)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <Link className="bell__all" to="/my/notifications" onClick={() => setOpen(false)}>
            See all notifications
          </Link>
        </div>
      ) : null}
    </div>
  );
}
